import React, { useState } from "react"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { SettingsPage } from "./page"
import { Form, FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import {
  disableBiometrics,
  isBiometricLoginAlreadyConfigured,
  isMobileNativeAndBiometricsCapable
} from "#/lib/carrot-api/MobileNativeClient"
import { UsaStates } from "../../utils/UsaStateOptions"
import { MessageDescriptor, useIntl } from "react-intl"
import { dayjs, type Dayjs } from "@carrotfertility/carotene-core"
import { AccountSettingsEnablePushNotificationsSettingDialog } from "#/components/views/account/AccountSettingsEnablePushNotificationsSettingDialog"
import {
  useIsMobileAndSupportsPushNotifications,
  useIsPushPermissionsEnabled
} from "#/components/views/modal/hooks/useMobileAppStatus"
import { useUpdateAccountSettings } from "#/components/views/account/hooks/useUpdateAccountSettings"
import { useFlags } from "launchdarkly-react-client-sdk"

type AccountSettingFormValues = {
  [s: string]: unknown
  dateOfBirth: Dayjs
  partnerDateOfBirth: Dayjs
}

export const SettingsContext = React.createContext<{
  isModalForm: boolean
  setIsModalForm: (arg: boolean) => void
  isBioauthEnabled: boolean
  setBioauthEnabled: (arg: boolean) => void
  fieldErrorFromServer: any
  showPartnerFieldsSuccessAlert?: boolean
  setShowPartnerFieldsSuccessAlert?: (arg: boolean) => void
}>({
  setIsModalForm: null,
  isModalForm: false,
  isBioauthEnabled: null,
  setBioauthEnabled: null,
  fieldErrorFromServer: null,
  showPartnerFieldsSuccessAlert: false,
  setShowPartnerFieldsSuccessAlert: null
})

export function SettingsContainer(): JSX.Element {
  const {
    email,
    firstName,
    lastName,
    preferredName,
    sex,
    genderIdentity,
    genderIdentityOther,
    dateOfBirth,
    phoneNumber,
    partnerFirstName,
    partnerLastName,
    partnerPreferredName,
    partnerDateOfBirth,
    partnerSex,
    partnerGenderIdentity,
    partnerGenderIdentityOther,
    partnerEmail,
    address1,
    address2,
    city,
    state,
    zip,
    internationalCountry,
    internationalAddressLine,
    isUsa,
    pushNotificationOptIn
  } = useCurrentUser()
  const { carrotConnectEnabled } = useFlags()
  const [isBioauthEnabled, setBioauthEnabled] = useState(false)
  const [isPushNotificationOptInEnabled, setIsPushNotificationOptInEnabled] = useState(pushNotificationOptIn)
  const [showPartnerFieldsSuccessAlert, setShowPartnerFieldsSuccessAlert] = useState(false)
  const { mutateAsync, error, isSuccess } = useUpdateAccountSettings(
    () => {
      setShowPartnerFieldsSuccessAlert(true)
      // Scroll to top when the alert panel appears
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    () => {
      setShowPartnerFieldsSuccessAlert(false)
    },
    carrotConnectEnabled
  )
  const [isModalForm, setIsModalForm] = useState(false)
  const intl = useIntl()
  const stateDefaultValue = state && isUsa ? formatStateValue(state) : state
  const internationalCountryDefaultValue =
    internationalCountry && !isUsa ? formatInternationalCountryValue(internationalCountry) : internationalCountry

  const { data: isPushPermissionsEnabled, isLoading: isPushPermissionsEnabledLoading } = useIsPushPermissionsEnabled()
  const { data: isMobileAndSupportsPushNotifications, isLoading: isMobileAndSupportsPushNotificationsLoading } =
    useIsMobileAndSupportsPushNotifications()

  const onSubmit: FormOnSubmitHandler<AccountSettingFormValues> = async (formFields, formMethods) => {
    if ((await isMobileNativeAndBiometricsCapable()) && (await isBiometricLoginAlreadyConfigured())) {
      // We only allow disabling biometric login at this time. To re-enable they have to go to the login screen
      if (!formFields.bioauthEnabled) {
        disableBiometrics()
        setBioauthEnabled(false)
      }
    }

    if ("pushNotificationOptIn" in formFields) {
      setIsPushNotificationOptInEnabled(formFields.pushNotificationOptIn as boolean)
    }

    // Our backend has logic that expects an empty string in order to clear this field
    const partnerDateOfBirthValue =
      formFields.partnerDateOfBirth === null ? "" : formatDateAsString(formFields.partnerDateOfBirth)

    // Convert "Yes"/"No" string values to boolean values
    const convertYesNoToBoolean = (value: any): boolean | null => {
      if (value === "Yes") return true
      if (value === "No") return false
      return value // Return original value if not "Yes" or "No"
    }

    const payload = {
      ...formFields,
      dateOfBirth: formatDateAsString(formFields.dateOfBirth),
      partnerDateOfBirth: partnerDateOfBirthValue,
      partnerAsDependent: convertYesNoToBoolean(formFields.partnerAsDependent),
      legallyMarried: convertYesNoToBoolean(formFields.legallyMarried),
      partnerFirstName: formFields.partnerFirstName === null ? "" : formFields.partnerFirstName,
      partnerLastName: formFields.partnerLastName === null ? "" : formFields.partnerLastName
    }
    try {
      await mutateAsync(payload)
      formMethods.reset({}, { keepValues: true })
    } catch (error) {
      // Don't reset form on error - keeps save button enabled, allows React Query to expose error via fieldErrorFromServer
    }
  }

  function formatDateAsString(date: Dayjs): string {
    const formattedDate = date.format("YYYY-MM-DD")
    return dayjs(formattedDate, "YYYY-MM-DD", true).isValid() ? formattedDate : ""
  }

  function formatStateValue(state: string): { value: string; label: string | MessageDescriptor } {
    return {
      value: state,
      label: getStateLabel(state)
    }
  }

  // when users are migrated from non-US to US geos, it's possible for string values to end up here that don't match with US states
  // without this check in place, user is blocked from accessing account settings in that scenario
  function getStateLabel(state: string | MessageDescriptor): string | MessageDescriptor {
    const usaState = UsaStates.find((stateOption) => stateOption.id === state)
    if (usaState) {
      return intl.formatMessage(usaState.label)
    }
    return state
  }

  function formatInternationalCountryValue(internationalCountry: string): { value: string; label: string } {
    return {
      value: internationalCountry,
      label: intl.formatDisplayName(internationalCountry, {
        type: "region",
        style: "long",
        fallback: "code"
      })
    }
  }

  const context = {
    setIsModalForm,
    isModalForm,
    isBioauthEnabled,
    setBioauthEnabled,
    fieldErrorFromServer: error,
    showPartnerFieldsSuccessAlert,
    setShowPartnerFieldsSuccessAlert
  }

  const showPushNotificationDialog =
    !isMobileAndSupportsPushNotificationsLoading &&
    !isPushPermissionsEnabledLoading &&
    isMobileAndSupportsPushNotifications &&
    isSuccess &&
    isPushNotificationOptInEnabled &&
    !isPushPermissionsEnabled

  return (
    <SettingsContext.Provider value={context}>
      <Form
        defaultValues={
          {
            email,
            firstName,
            lastName,
            preferredName,
            sex,
            genderIdentity,
            genderIdentityOther,
            dateOfBirth: dayjs(dateOfBirth),
            phoneNumber,
            partnerFirstName,
            partnerLastName,
            partnerPreferredName,
            partnerDateOfBirth: partnerDateOfBirth ? dayjs(partnerDateOfBirth) : null,
            partnerSex,
            partnerGenderIdentity,
            partnerGenderIdentityOther,
            partnerEmail,
            address1,
            address2,
            city,
            state: stateDefaultValue,
            zip,
            internationalCountry: internationalCountryDefaultValue,
            internationalAddressLine,
            pushNotificationOptIn: pushNotificationOptIn
          } as AccountSettingFormValues
        }
        onSubmit={(...args) => (isModalForm ? null : onSubmit(...args))}
      >
        <SettingsPage />
        {showPushNotificationDialog && <AccountSettingsEnablePushNotificationsSettingDialog initialOpen={true} />}
      </Form>
    </SettingsContext.Provider>
  )
}
